/**
 * Notificarile scurte („Adaugat in cos", „Adresa salvata"), la marginea de jos.
 *
 * Exista pentru un defect de ecran, nu de cod: confirmarea adaugarii in cos
 * statea intr-o banda in CAPUL paginii de meniu. Pe un meniu cu 477 de produse,
 * clientul care apasa „+" la mijlocul listei nu vedea nimic — banda era cu trei
 * ecrane mai sus — si apasa din nou, deci comanda pleca cu doua bucati.
 *
 * Reguli:
 *  - apar deasupra barei de cos, nu sub ea: `--da-bottom-bar-h` e publicata de
 *    `Layout` cu inaltimea reala a barei lipite;
 *  - se anunta cititoarelor de ecran o singura data (`aria-live="polite"`);
 *  - o notificare noua cu aceeasi cheie o INLOCUIESTE pe cea veche: cinci
 *    apasari rapide pe „+" dau o singura notificare cu cantitatea la zi, nu
 *    cinci care se suprapun;
 *  - dispar singure, dar raman la cursor/deget (hover, focus) ca sa poata fi
 *    apasat butonul din ele.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { IconCheck, IconClose, IconInfo, IconWarning } from "./icons";

export type ToastTone = "success" | "info" | "warning";

export interface ToastInput {
  /** Aceeasi cheie inlocuieste notificarea anterioara in loc sa o dubleze. */
  key?: string;
  title: string;
  description?: string;
  tone?: ToastTone;
  action?: { label: string; onClick: () => void };
  /** Milisecunde; implicit 3500. */
  durationMs?: number;
}

interface ToastEntry extends ToastInput {
  id: number;
  key: string;
}

interface ToastContextValue {
  toast: (input: ToastInput) => void;
  dismiss: (key: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (value === null) throw new Error("useToast a fost apelat in afara lui ToastProvider.");
  return value;
}

const MAX_VISIBLE = 3;
const DEFAULT_DURATION_MS = 3500;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<ToastEntry[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((key: string) => {
    setEntries((current) => current.filter((entry) => entry.key !== key));
  }, []);

  const toast = useCallback((input: ToastInput) => {
    const key = input.key ?? `toast-${nextId.current}`;
    const entry: ToastEntry = { ...input, key, id: nextId.current++ };
    setEntries((current) => [...current.filter((item) => item.key !== key), entry].slice(-MAX_VISIBLE));
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 z-50 flex flex-col items-center gap-2 px-4"
        style={{ bottom: "calc(var(--da-bottom-bar-h, 0px) + env(safe-area-inset-bottom) + 0.75rem)" }}
        aria-live="polite"
        role="status"
      >
        {entries.map((entry) => (
          <ToastCard key={entry.id} entry={entry} onDismiss={() => dismiss(entry.key)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ entry, onDismiss }: { entry: ToastEntry; onDismiss: () => void }) {
  const [held, setHeld] = useState(false);
  const duration = entry.durationMs ?? DEFAULT_DURATION_MS;

  useEffect(() => {
    if (held) return;
    const timer = window.setTimeout(onDismiss, duration);
    return () => window.clearTimeout(timer);
    // `entry.id` in dependinte: aceeasi cheie cu continut nou reporneste ceasul.
  }, [held, duration, onDismiss, entry.id]);

  const tone = entry.tone ?? "success";
  const Glyph = tone === "success" ? IconCheck : tone === "warning" ? IconWarning : IconInfo;

  return (
    <div
      className="da-toast pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-card px-3.5 py-3 text-sm shadow-lg"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
    >
      <span
        className={[
          "mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full",
          tone === "success" ? "bg-success text-white" : tone === "warning" ? "bg-warning text-white" : "bg-brand text-brand-contrast",
        ].join(" ")}
      >
        <Glyph size={15} strokeWidth={2.4} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{entry.title}</p>
        {entry.description === undefined || entry.description.length === 0 ? null : (
          <p className="mt-0.5 opacity-80">{entry.description}</p>
        )}
      </div>
      {entry.action === undefined ? null : (
        <button
          type="button"
          onClick={() => {
            entry.action?.onClick();
            onDismiss();
          }}
          className="da-toast-action shrink-0 self-center rounded-control px-2.5 py-1.5 text-sm font-bold"
        >
          {entry.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Închide notificarea"
        className="-mr-1 shrink-0 self-center rounded-control p-1 opacity-70 hover:opacity-100"
      >
        <IconClose size={16} />
      </button>
    </div>
  );
}
